import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, Check, Share2, Trash2, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getSession } from '../../lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const ROLE_BADGES = {
  owner: { label: 'Owner', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  editor: { label: 'Can edit', bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  viewer: { label: 'Can view', bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' },
};

const AVATAR_COLORS = [
  'bg-teal-500', 'bg-indigo-500', 'bg-rose-500', 'bg-amber-500',
  'bg-emerald-500', 'bg-purple-500', 'bg-sky-500', 'bg-orange-500',
];

function getAvatarColor(userId) {
  if (!userId) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

export default function ShareModal({ isOpen, onClose, tripId, tripTitle, destination }) {
  const { user } = useAuth();
  const [editorLink, setEditorLink] = useState('');
  const [viewerLink, setViewerLink] = useState('');
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null); // 'editor' | 'viewer'
  const [error, setError] = useState(null);
  const [removingUserId, setRemovingUserId] = useState(null);

  // Get auth headers by reading token from localStorage (fast) or getSession() (fallback)
  const getAuthHeaders = useCallback(async () => {
    const headers = { 'Content-Type': 'application/json' };
    try {
      // Fast path: read from localStorage
      const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
      if (storageKey) {
        const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
        if (stored?.access_token) {
          headers['Authorization'] = `Bearer ${stored.access_token}`;
          return headers;
        }
      }
      // Fallback: getSession()
      const session = await getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
    } catch { /* proceed without auth */ }
    return headers;
  }, []);

  // Generate invite links and fetch collaborators on mount
  useEffect(() => {
    if (!isOpen || !tripId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const headers = await getAuthHeaders();
        const [editorRes, viewerRes, collabRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/trips/invite`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ tripId, role: 'editor' }),
          }),
          fetch(`${BACKEND_URL}/api/trips/invite`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ tripId, role: 'viewer' }),
          }),
          fetch(`${BACKEND_URL}/api/trips/${tripId}/collaborators`, {
            headers,
          }),
        ]);

        if (editorRes.ok) {
          const data = await editorRes.json();
          setEditorLink(data.url || `${window.location.origin}/trip/invite/${data.token}`);
        } else {
          // Fallback: generate a simple hash-based share link
          setEditorLink(`${window.location.origin}/trip/${tripId}?mode=edit`);
        }
        if (viewerRes.ok) {
          const data = await viewerRes.json();
          setViewerLink(data.url || `${window.location.origin}/trip/invite/${data.token}`);
        } else {
          setViewerLink(`${window.location.origin}/trip/${tripId}`);
        }
        if (collabRes.ok) {
          const data = await collabRes.json();
          setCollaborators(Array.isArray(data) ? data : data.collaborators || []);
        }
      } catch (err) {
        setError('Failed to load sharing settings');
        console.error('ShareModal fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, tripId, getAuthHeaders]);

  const handleCopy = useCallback(async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  }, []);

  const handleRemove = useCallback(async (userId) => {
    setRemovingUserId(userId);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/trips/${tripId}/collaborators/${userId}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        setCollaborators(prev => prev.filter(c => c.user_id !== userId));
      }
    } catch (err) {
      console.error('Failed to remove collaborator:', err);
    } finally {
      setRemovingUserId(null);
    }
  }, [tripId, getAuthHeaders]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop — click to close */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Share trip</h2>
            <p className="text-sm text-gray-500 mt-0.5 truncate max-w-[350px]">
              {tripTitle || destination || 'Untitled Trip'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mb-3 px-3 py-2 bg-red-50 text-red-600 text-sm rounded-lg">
            {error}
          </div>
        )}

        {/* Share Links */}
        <div className="px-6 space-y-3">
          <LinkRow
            label="Can edit"
            link={editorLink}
            loading={loading}
            copied={copiedField === 'editor'}
            onCopy={() => handleCopy(editorLink, 'editor')}
          />
          <LinkRow
            label="Can view"
            link={viewerLink}
            loading={loading}
            copied={copiedField === 'viewer'}
            onCopy={() => handleCopy(viewerLink, 'viewer')}
          />
          <p className="text-xs text-gray-400">Links expire in 30 days</p>
        </div>

        {/* Collaborators */}
        <div className="px-6 pt-5 pb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">People with access</h3>
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-gray-200" />
                  <div className="flex-1">
                    <div className="h-3.5 w-24 bg-gray-200 rounded" />
                    <div className="h-3 w-32 bg-gray-100 rounded mt-1" />
                  </div>
                </div>
              ))}
            </div>
          ) : collaborators.length === 0 ? (
            <p className="text-sm text-gray-400">Only you have access</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {collaborators.map((collab) => {
                const role = collab.role || 'viewer';
                const badge = ROLE_BADGES[role] || ROLE_BADGES.viewer;
                const isOwner = role === 'owner';
                const id = collab.userId || collab.user_id;
                const displayName = collab.name || collab.display_name;
                const email = collab.email;

                return (
                  <div key={id} className="flex items-center gap-3 py-1.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${getAvatarColor(id)}`}>
                      {getInitials(displayName || email)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {displayName || email || 'Unknown'}
                      </p>
                      {email && displayName && (
                        <p className="text-xs text-gray-400 truncate">{email}</p>
                      )}
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${badge.bg} ${badge.text} ${badge.border}`}>
                      {badge.label}
                    </span>
                    {!isOwner && (
                      <button
                        onClick={() => handleRemove(id)}
                        disabled={removingUserId === id}
                        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LinkRow({ label, link, loading, copied, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600 w-16 shrink-0">{label}</span>
      <div className="flex-1 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
        {loading ? (
          <div className="h-3.5 w-full bg-gray-200 rounded animate-pulse" />
        ) : (
          <span className="text-xs text-gray-500 truncate flex-1 max-w-[160px]">{link}</span>
        )}
        <button
          onClick={onCopy}
          disabled={loading || !link}
          className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors disabled:opacity-50
            bg-teal-600 hover:bg-teal-700 text-white"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              Copy
            </>
          )}
        </button>
      </div>
    </div>
  );
}
